import {FC, MouseEvent, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {ContentCopyOutlined, PersonOutlineOutlined} from '@mui/icons-material';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {Link, useParams} from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {useIntl} from 'react-intl';
import {locationsApi} from '../../../../api';
import {useAppDispatch} from '../../../../app/hooks';
import {NotifyError, NotifySuccess} from '../../../../utils/notification';
import {AppDispatch} from '../../../../types';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {clientCardSx, factWithActionSx, nameLinkSx} from './ClientCard.sx';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {ClientCardProps} from './ClientCard.types';

type DeleteUserFromLocationMutationTrigger = ReturnType<
    typeof locationsApi.useDeleteUserFromLocationMutation
>[0];

const handleDeleteClient =
    (
        trigger: DeleteUserFromLocationMutationTrigger,
        dispatch: AppDispatch,
        locationId: number,
        userId: number,
        businessId: number
    ) =>
        async () => {
            const data = {
                businessId,
                locationId,
                userId,
            };
            dispatch(preloaderChangeStatus(true));
            try {
                await trigger(data).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ClientCard: FC<ClientCardProps> = ({
                                                    id,
                                                    parkingAddress,
                                                    businessId,
                                                    firstName,
                                                    lastName,
                                                    chargePoint,
                                                    canCreateDeleteUsers = true,
                                                }) => {
    // The menu exists only to offer this action, so the permission decides whether it appears.
    // Where a flag is also passed in, both must hold: the parent says whether the action belongs
    // on this screen, the permission says whether this person may perform it - and the prop
    // defaults to true, so on its own it fails open.
    const canDetachClient = useCan('locations.assignUser');
    const intl = useIntl();
    const {id: locationId} = useParams();
    const [trigger] = locationsApi.useDeleteUserFromLocationMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(chargePoint.chargePointId), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = (
        <Button
            sx={clientCardSx.menuBtn}
            onClick={handleDeleteClient(
                trigger,
                dispatch,
                locationId ? +locationId : 0,
                id,
                businessId
            )}
        >
            <Typography sx={clientCardSx.menuBtnText}>
                {intl.formatMessage({id: 'delete'})}
            </Typography>
        </Button>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<PersonOutlineOutlined/>}
                person
                title={(
                    <Box
                        component={Link}
                        to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(id)])}
                        sx={nameLinkSx}
                    >
                        {`${firstName} ${lastName}`}
                    </Box>
                )}
                subtitle={chargePoint?.chargePointId}
                action={canCreateDeleteUsers && canDetachClient && (
                    <MenuButton handleClick={handleClick}/>
                )}
            />
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'parkingPlace'}),
                        value: parkingAddress?.parkingPlace ?? '0',
                    },
                    {
                        label: intl.formatMessage({id: 'chargePointId'}),
                        value: (
                            <Box
                                component="span"
                                sx={factWithActionSx}
                            >
                                {chargePoint?.chargePointId ? (
                                    <Box
                                        component={Link}
                                        to={AppRouteSpace([
                                            AppRouteEnum.app,
                                            AppRouteEnum.chargers,
                                            String(chargePoint.id),
                                        ])}
                                        sx={nameLinkSx}
                                    >
                                        {chargePoint.chargePointId}
                                    </Box>
                                ) : '-'}
                                {chargePoint?.chargePointId && (
                                    <IconButton
                                        onClick={copyToClipboard}
                                        sx={clientCardSx.iconBtn}
                                    >
                                        <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                    </IconButton>
                                )}
                            </Box>
                        ),
                    },
                ]}
            />
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<PersonOutlineOutlined/>}
                    title={`${firstName} ${lastName}`.trim()}
                    subtitle={chargePoint?.chargePointId}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={clientCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </TableCard>
    );
};
